import { useRef, useEffect, useState, KeyboardEvent, ChangeEvent, useCallback, MouseEvent } from 'react';
import { FaSearch } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useMapStore } from '../../lib/map/store';
import { getGeocode, getLatLng } from 'use-places-autocomplete';
import { isLocationWithinServiceArea } from '../../lib/map/utils';
import { searchBounds } from '../../lib/map/constants';

// Define Chiang Mai's boundary coordinates - use actual service area bounds from constants
const CHIANG_MAI_BOUNDS = searchBounds;

// Calculate the center point of Chiang Mai from bounds
const CHIANG_MAI_CENTER = {
  lat: (CHIANG_MAI_BOUNDS.north + CHIANG_MAI_BOUNDS.south) / 2,
  lng: (CHIANG_MAI_BOUNDS.east + CHIANG_MAI_BOUNDS.west) / 2
};

// Create a more aggressive location bias with smaller radius
const createLocationBias = () => {
  // If Google Maps API is loaded, create a proper CircleLiteral object
  if (window.google && window.google.maps) {
    // Try to explicitly create a bounds object first
    try {
      // Create a smaller bounds object focused on Chiang Mai city center
      return new google.maps.LatLngBounds(
        new google.maps.LatLng(CHIANG_MAI_BOUNDS.south, CHIANG_MAI_BOUNDS.west),
        new google.maps.LatLng(CHIANG_MAI_BOUNDS.north, CHIANG_MAI_BOUNDS.east)
      );
    } catch (error) {
      // Fallback to circle if bounds creation fails
      console.log('[SearchBar] Using circle bias as fallback');
      return {
        center: CHIANG_MAI_CENTER,
        radius: 20000 // Reduced to 20km radius for tighter search area
      };
    }
  }
  
  // Fallback if Google Maps not yet loaded
  return CHIANG_MAI_CENTER;
};

// SearchBar component that uses Zustand store
export const SearchBar: React.FC<{
  inputRef?: React.RefObject<HTMLInputElement>;
  className?: string;
  onSearch?: (query: string) => void;
  onShowMapModal?: () => void;
}> = ({ inputRef, className = '', onSearch, onShowMapModal }) => {
  // Debug log to check if callback exists
  console.log('[SearchBar] onShowMapModal exists:', !!onShowMapModal);
  
  const { t } = useTranslation();
  const { 
    searchQuery, 
    isSearching, 
    setSearchQuery, 
    setIsSearching,
    setCurrentStep
  } = useMapStore();
  
  // Track if a place was selected to prevent double actions
  const placeSelectedRef = useRef(false);
  
  // Track if a suggestion was selected
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  
  // Add state to store suggestions
  const [suggestions, setSuggestions] = useState<any[]>([]);
  
  // Add state to track input value
  const [inputValue, setInputValue] = useState(searchQuery);
  
  // Add state to track if API is ready
  const [isReady, setIsReady] = useState(false);
  
  // Custom input ref if one is not provided
  const localInputRef = useRef<HTMLInputElement>(null);
  // Use the provided inputRef or our local one
  const actualInputRef = inputRef || localInputRef;
  
  // Add Google Maps API loading state
  const [mapsApiLoaded, setMapsApiLoaded] = useState(false);
  
  // Add reference for AutocompleteSuggestion and AutocompleteService
  const autocompleteSuggestionRef = useRef<google.maps.places.AutocompleteSuggestion | null>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  
  // Check if Google Maps API is loaded
  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        console.log('[SearchBar] Google Maps API detected as loaded');
        setMapsApiLoaded(true);
        
        // Initialize AutocompleteService when API is loaded
        if (!autocompleteServiceRef.current) {
          try {
            console.log('[SearchBar] Initializing AutocompleteService');
            autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
            setIsReady(true);
          } catch (err) {
            console.error('[SearchBar] Error initializing AutocompleteService:', err);
          }
        }
        
        return true;
      }
      return false;
    };
    
    // Check immediately
    if (checkGoogleMapsLoaded()) return;
    
    // If not loaded, check again after a delay
    const timer = setInterval(() => {
      if (checkGoogleMapsLoaded()) {
        clearInterval(timer);
      }
    }, 500);
    
    return () => clearInterval(timer);
  }, []);

  // Function to fetch predictions
  const fetchPredictions = useCallback(async (input: string) => {
    if (!autocompleteServiceRef.current || !input.trim()) {
      setSuggestions([]);
      return;
    }
    
    try {
      console.log('[SearchBar] Fetching predictions for:', input);
      setSuggestionSelected(false); // Reset the selected state when fetching new predictions
      
      // Create bounds object if Google Maps is loaded
      let bounds: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | undefined = undefined;
      if (window.google && window.google.maps) {
        bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(CHIANG_MAI_BOUNDS.south, CHIANG_MAI_BOUNDS.west),
          new google.maps.LatLng(CHIANG_MAI_BOUNDS.north, CHIANG_MAI_BOUNDS.east)
        );
      }
      
      // Create request with location bias - using the correct type for AutocompleteService
      const request: google.maps.places.AutocompletionRequest = {
        input,
        componentRestrictions: { country: 'th' },
        types: ['geocode', 'establishment'],
        bounds,
        // Convert center to proper LatLng object
        location: window.google && window.google.maps 
          ? new google.maps.LatLng(CHIANG_MAI_CENTER.lat, CHIANG_MAI_CENTER.lng)
          : undefined,
        radius: 20000,  // 20km radius
        language: 'en'
      };
      
      // Get predictions using AutocompleteService
      autocompleteServiceRef.current.getPlacePredictions(
        request,
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            console.log('[SearchBar] Raw predictions:', predictions);
            
            // Filter predictions if needed
            const filteredPredictions = predictions.filter((suggestion) => {
              if (!suggestion.description) return false;
              
              // Check if in Thailand
              const inThailand = suggestion.description.toLowerCase().includes('thailand') ||
                              suggestion.description.toLowerCase().includes('thai');
                              
              // Log filtering decision
              console.log(`[SearchBar] Prediction "${suggestion.description}" - In Thailand: ${inThailand}`);
              
              return inThailand;
            });
            
            console.log('[SearchBar] Filtered predictions:', filteredPredictions);
            setSuggestions(filteredPredictions);
          } else {
            console.log('[SearchBar] No predictions or error:', status);
            setSuggestions([]);
          }
        }
      );
    } catch (error) {
      console.error('[SearchBar] Error fetching predictions:', error);
      setSuggestions([]);
    }
  }, []);

  // Handle input change
  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setSearchQuery(value);
    
    // Always reset selected state on input change
    if (suggestionSelected) {
      setSuggestionSelected(false);
    }
    
    // Clear suggestions if input is empty
    if (!value.trim()) {
      setSuggestions([]);
      return;
    }
    
    // Fetch predictions if API is ready and value is not empty
    if (isReady && value.trim()) {
      fetchPredictions(value);
    }
  }, [fetchPredictions, isReady, setSearchQuery, suggestionSelected]);

  // Sync with search query from store
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  // Sync input ref with the one provided
  useEffect(() => {
    if (inputRef?.current && actualInputRef.current) {
      // If inputRef was provided, ensure we use it
      const currValue = actualInputRef.current.value;
      if (currValue !== inputValue) {
        setInputValue(currValue);
      }
    }
  }, [inputRef, actualInputRef, inputValue]);

  // Handle input focus
  const handleInputFocus = useCallback(() => {
    // If already selected a suggestion, don't show suggestions on focus
    if (suggestionSelected) {
      setSuggestions([]);
    } else if (inputValue.trim() && isReady) {
      fetchPredictions(inputValue);
    }
  }, [fetchPredictions, inputValue, isReady, suggestionSelected]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        // Skip if a place was just selected from autocomplete
        if (placeSelectedRef.current) {
          console.log('[SearchBar] Skipping Enter key handler - place just selected');
          placeSelectedRef.current = false;
          return;
        }
        
        // Call custom onSearch handler if provided, otherwise use default behavior
        if (onSearch) {
          onSearch(inputValue);
          // Set map selection interface
          setCurrentStep(1);
          // Notify parent component to switch to map modal window
          if (onShowMapModal) {
            console.log('[SearchBar] Calling onShowMapModal from Enter key');
            onShowMapModal();
          }
        } else {
          useMapStore.setState({ isProgrammaticUpdate: true });
          setIsSearching(true);
          // Set map selection interface
          setCurrentStep(1);
          // Notify parent component to switch to map modal window
          if (onShowMapModal) {
            console.log('[SearchBar] Calling onShowMapModal from Enter key (default)');
            onShowMapModal();
          }
        }
      }
    }
  }, [inputValue, onSearch, onShowMapModal, setCurrentStep, setIsSearching]);

  const handleSearchClick = useCallback(() => {
    if (inputValue.trim()) {
      // Skip if a place was just selected from autocomplete
      if (placeSelectedRef.current) {
        console.log('[SearchBar] Skipping search click handler - place just selected');
        placeSelectedRef.current = false;
        return;
      }
      
      // Call custom onSearch handler if provided, otherwise use default behavior
      if (onSearch) {
        onSearch(inputValue);
        // Set map selection interface
        setCurrentStep(1);
        // Notify parent component to switch to map modal window
        if (onShowMapModal) {
          console.log('[SearchBar] Calling onShowMapModal from search click');
          onShowMapModal();
        }
      } else {
        useMapStore.setState({ isProgrammaticUpdate: true });
        setIsSearching(true);
        // Set map selection interface
        setCurrentStep(1);
        // Notify parent component to switch to map modal window
        if (onShowMapModal) {
          console.log('[SearchBar] Calling onShowMapModal from search click (default)');
          onShowMapModal();
        }
      }
    }
  }, [inputValue, onSearch, onShowMapModal, setCurrentStep, setIsSearching]);

  // Handle selecting a suggestion
  const handleSelectSuggestion = async (suggestion: any) => {
    try {
      // Immediately clear suggestions to prevent displaying during processing
      setSuggestions([]);
      
      // Mark suggestion as selected
      setSuggestionSelected(true);
      
      // Get the place ID and description
      const placeId = suggestion.place_id;
      const description = suggestion.description || '';
      
      // Log the selected suggestion to help debugging
      console.log('[SearchBar] Selected suggestion:', suggestion);
      
      // Extract place name (usually the first part of the description, before the comma)
      const shortName = description.split(',')[0].trim();
      
      // Only set the short place name to the input field
      setInputValue(shortName);
      setSearchQuery(shortName);
      
      // Mark that a place was selected to prevent double actions
      placeSelectedRef.current = true;
      
      console.log('[SearchBar] Place selected with place_id:', placeId);
      
      // Get geocode details of the place
      const results = await getGeocode({ placeId });
      if (!results.length) {
        throw new Error('No geocode results found');
      }
      
      const result = results[0];
      const { lat, lng } = getLatLng(result);
      
      console.log('[SearchBar] Selected place position:', { lat, lng });
      
      // Verify the location is within Chiang Mai area
      const isWithinChiangMai = isLocationWithinServiceArea({ lat, lng });
      if (!isWithinChiangMai) {
        console.warn('[SearchBar] Selected location is outside Chiang Mai service area');
        
        // Show a warning message about location being outside service area
        const { map } = useMapStore.getState();
        if (map) {
          try {
            // Import showMapToast function from mapControls
            import('../../lib/map/mapControls').then(controls => {
              controls.showMapToast(
                { current: map } as React.MutableRefObject<google.maps.Map>, 
                t('map.locationOutsideToast', 'This location is outside our Chiang Mai delivery area. Please select a location within Chiang Mai.'),
                5000
              );
            }).catch(err => {
              console.error('[SearchBar] Error showing map toast:', err);
            });
          } catch (err) {
            console.error('[SearchBar] Error importing map controls:', err);
          }
        }
      }
      
      // Extract address from the place
      const placeAddress = result.formatted_address || description;
      
      // Use the short name
      const placeName = shortName;
      
      // Parse address components to extract city, state, etc.
      let city = '';
      let state = '';
      let postalCode = '';
      let country = '';
      
      if (result.address_components) {
        for (const component of result.address_components) {
          const types = component.types;
          
          if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            state = component.long_name;
          } else if (types.includes('postal_code')) {
            postalCode = component.long_name;
          } else if (types.includes('country')) {
            country = component.long_name;
          }
        }
      }
      
      console.log('[SearchBar] Parsed address components:', { city, state, postalCode, country });
      
      // Create an addressDetails object with the complete selection data
      const addressDetails = {
        formatted_address: placeAddress,
        name: placeName,
        city,
        state,
        postalCode,
        country,
        latitude: lat,
        longitude: lng
      };
      
      // Create a complete selection data object to ensure consistency
      const selectionData = {
        position: { lat, lng },
        address: placeAddress,
        locationName: placeName,
        addressDetails
      };
      
      // Store selected location in localStorage for additional reliability
      try {
        localStorage.setItem('temp_selected_location', JSON.stringify(selectionData));
        console.log('[SearchBar] Saved selected location to localStorage for reliability');
      } catch (err) {
        console.error('[SearchBar] Failed to save to localStorage:', err);
      }
      
      // CRITICAL: Disable any further automatic location updates to prevent override
      // This is a key change to prevent the address from jumping
      const currentMapState = useMapStore.getState();
      
      // Update map store with all data in a single, atomic operation to prevent race conditions
      // and PREVENT any further updates by setting isFromSearch = true
      useMapStore.setState({ 
        isProgrammaticUpdate: true, 
        isFromSearch: true,
        markerPosition: { lat, lng },
        lastPosition: { lat, lng },
        address: placeAddress,
        locationName: placeName,
        addressDetails,
        currentStep: 1,
        // Stop any further processing that might override this selection
        isAddressLocked: true  // Add a new flag to indicate this address is locked in
      });
      
      console.log('[SearchBar] Updated map store with complete address data in single operation');
      
      // If map exists, force it to pan to the selected location
      const { map } = useMapStore.getState();
      if (map) {
        console.log('[SearchBar] Map instance found, forcing position update');
        
        // Remove any existing idle listeners to prevent race conditions
        google.maps.event.clearListeners(map, 'idle');
        
        // Update map center and zoom
        map.setCenter({ lat, lng });
        map.setZoom(18); // Higher zoom level for better precision
        
        // Check if the selected location is within service area and show warning if needed
        try {
          // Import needed utilities
          import('../../lib/map/utils').then(utils => {
            // Check if within service area
            const isWithin = utils.isLocationWithinServiceArea({ lat, lng });
            useMapStore.getState().setIsWithinServiceArea(isWithin);
            
            console.log('[SearchBar] Selected location within service area:', isWithin);
            
            // Show warning toast if outside service area
            if (!isWithin && map) {
              console.log('[SearchBar] Selected location is outside service area, showing warning');
              import('../../lib/map/mapControls').then(controls => {
                controls.showMapToast({ current: map } as React.MutableRefObject<google.maps.Map>, 
                  'Location is outside our delivery area. Please select another location.',
                  5000
                );
              });
            }
          });
        } catch (error) {
          console.error('[SearchBar] Error checking service area:', error);
        }
        
        // Find nearby places for the selected location
        try {
          // Use a setTimeout to ensure the map is properly updated before searching
          setTimeout(() => {
            if (map) {
              import('../../lib/map/utils').then(utils => {
                console.log('[SearchBar] Searching for nearby places at selected location');
                utils.findNearbyPlacesModern(map, { lat, lng }, placeAddress)
                  .then(places => {
                    if (places && places.length > 0) {
                      console.log('[SearchBar] Found nearby places:', places.length);
                      useMapStore.getState().setAlternateAddresses(places);
                    } else {
                      console.log('[SearchBar] No nearby places found');
                    }
                  })
                  .catch(error => {
                    console.error('[SearchBar] Error finding nearby places:', error);
                  });
              });
            }
          }, 500);
        } catch (error) {
          console.error('[SearchBar] Error finding nearby places:', error);
        }
        
        // Force a redraw
        setTimeout(() => {
          google.maps.event.trigger(map, 'resize');
        }, 100);
      } else {
        console.warn('[SearchBar] No map instance in store when place selected');
      }
      
      // Check if we need to notify parent to show the map view
      if (onShowMapModal) {
        console.log('[SearchBar] Calling onShowMapModal to show map');
        
        try {
          // Important: Use a small delay before opening map to ensure store updates are processed
          setTimeout(() => {
            try {
              // Execute onShowMapModal with delay to ensure store is updated first
              if (onShowMapModal) {
                onShowMapModal();
                console.log('[SearchBar] onShowMapModal executed with delay');
              }
            } catch (error) {
              console.error('[SearchBar] Error in delayed onShowMapModal call:', error);
            }
          }, 300);
        } catch (error) {
          console.error('[SearchBar] Error executing onShowMapModal:', error);
        }
      } else {
        console.warn('[SearchBar] onShowMapModal callback is undefined - map view will not be shown!');
      }
      
      // If onSearch is provided, also call it with the selected place data
      if (onSearch) {
        console.log('[SearchBar] Calling onSearch with place data');
        try {
          // Call onSearch with the full place data
          onSearch(placeName || placeAddress);
          console.log('[SearchBar] onSearch executed successfully');
        } catch (error) {
          console.error('[SearchBar] Error executing onSearch:', error);
        }
      }
      
      // Reset programmatic update flag in store after a delay
      setTimeout(() => {
        // Keep isFromSearch true but reset programmatic update flag
        // But KEEP isAddressLocked true to prevent overrides
        useMapStore.setState({ 
          isProgrammaticUpdate: false,
          isFromSearch: true
        });
        
        // Log final state for debugging
        console.log('[SearchBar] Final map store state after place selection:', useMapStore.getState());
        
        // Reset place selected flag after a delay
        setTimeout(() => {
          placeSelectedRef.current = false;
        }, 500);
      }, 800);
    } catch (error) {
      console.error('[SearchBar] Error selecting place:', error);
      placeSelectedRef.current = false;
    }
  };

  // Add at the beginning of the render function to debug UI state
  console.log('[SearchBar] Render state:', {
    suggestions: suggestions.length,
    suggestionSelected,
    inputValue
  });

  // Add logging in the render function right before returning JSX
  console.log('[SearchBar] Rendering suggestions UI with:', {
    showingList: suggestions.length > 0 && !suggestionSelected,
    suggestionsCount: suggestions.length,
    suggestionSelected
  });

  return (
    <div className="relative w-full mb-0" style={{ zIndex: 10, minHeight: '44px' }}>
      <div className="relative w-full flex" style={{ minHeight: '44px' }}>
        <input
          ref={actualInputRef}
          type="text"
          placeholder={t("map.searchPlaceholder")}
          className={`w-full min-h-[44px] pl-4 pr-12 border border-gray-300 rounded-[16px] focus:outline-none focus:border-blue-500 bg-white ${className} block visible opacity-100`}
          style={{ 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
            fontSize: '16px',
            pointerEvents: 'auto'
          }}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={false} // 确保输入框不被禁用
          autoComplete="off"
          onFocus={handleInputFocus}
        />
        
        {isSearching ? (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <div className="w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <button 
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-transparent border-none focus:outline-none"
            onClick={handleSearchClick}
            aria-label={t("map.search")}
            style={{ pointerEvents: 'auto', zIndex: 20 }}
          >
            <FaSearch className="text-gray-600 w-5 h-5" />
          </button>
        )}
        
        {/* Only show suggestions when we have them and not in selected state */}
        {suggestions.length > 0 && !suggestionSelected && (
          <ul className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border-2 border-blue-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {suggestions.map((suggestion, index) => {
              // Extract the main place name (first part before the comma)
              const mainName = suggestion.description.split(',')[0].trim();
              // Use the full address as secondary text
              const fullAddress = suggestion.description;
              
              return (
                <li
                  key={suggestion.place_id || `suggestion-${index}`}
                  onClick={() => {
                    console.log('[SearchBar] Suggestion clicked:', suggestion);
                    handleSelectSuggestion(suggestion);
                  }}
                  className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-gray-700 text-sm border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium">{mainName}</div>
                  <div className="text-xs text-gray-500 mt-1">{fullAddress}</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}; 